import { PageLink } from '../../../../../../_metronic/layout/core'
import { DASHBOARD_API } from '../../Dashboard/core/consts'
import { DEPOSIT_RAMP, PAYOUT_RAMP } from '../../Dashboard/core/seriesColors'
import { DepositGroup, PayoutKind } from './response.types'
import { PayoutKindFilter, TimelineMode } from './types'

export const TIMELINE_API = `${DASHBOARD_API}/timeline`
export const DASHBOARD_ROUTE = '/matching/dashboard'
export const TIMELINE_ROUTE = `${DASHBOARD_ROUTE}/timeline`

// PageTitle keys its effect on the breadcrumbs identity — the list must be a
// module-level constant, never an inline array.
export const TIMELINE_BREADCRUMBS: PageLink[] = [
  { title: 'Состояние мэтчинга', path: DASHBOARD_ROUTE, isActive: false },
  { title: '', path: '', isActive: false, isSeparator: true },
]

export const STEP_OPTIONS = [
  { value: 15 * 60 * 1000, label: '15 мин' },
  { value: 60 * 60 * 1000, label: '1 час' },
  { value: 3 * 60 * 60 * 1000, label: '3 часа' },
  { value: 6 * 60 * 60 * 1000, label: '6 часов' },
  { value: 24 * 60 * 60 * 1000, label: '24 часа' },
]

export const MODES: TimelineMode[] = ['uncovered', 'covered', 'all']

export const MODE_LABEL: Record<TimelineMode, string> = {
  uncovered: 'Непокрытый',
  covered: 'Покрытый',
  all: 'Вместе',
}

export const PAYOUT_KIND_FILTERS: PayoutKindFilter[] = [
  'rejected',
  'unmatched',
  'matched_unpaid',
  'other',
]

export const PAYOUT_KIND_FILTER_LABEL: Record<PayoutKindFilter, string> = {
  rejected: 'Не приняты',
  unmatched: 'Приняты, 0 мэтчей',
  matched_unpaid: 'Мэтчи были, 0 успешных, отменена',
  other: 'Остальные',
}

export const PAYOUT_KIND_LABEL: Record<PayoutKind, string> = {
  rejected: 'Не принята на входе',
  unmatched: 'Принята, мэтчей не было',
  matched_unpaid: 'Мэтчи были, ни одного успешного',
  paid: 'Закрыта',
  live: 'В работе',
}

// Выплаты — зелёная рампа, депозиты — синяя: правило цветов всего дашборда.
// Не взятое в работу — приглушённый серый оттенок своей стороны: серо-зелёный
// бар у не принятой выплаты, серо-синие точки у депозита без пары.
export const REFUSED_PAYOUT_COLOR = '#A7BDB0'
export const REFUSED_DEPOSIT_COLOR = '#93A6B8'

export const PAYOUT_KIND_COLOR: Record<PayoutKind, string> = {
  rejected: REFUSED_PAYOUT_COLOR,
  unmatched: PAYOUT_RAMP[1],
  matched_unpaid: PAYOUT_RAMP[3],
  paid: PAYOUT_RAMP[4],
  live: PAYOUT_RAMP[2],
}

/** Номер поверх бара: светлые чернила на тёмных барах, тёмные на светлых. */
export const PAYOUT_KIND_INK: Record<PayoutKind, string> = {
  rejected: '#3F4254',
  unmatched: '#3F4254',
  matched_unpaid: '#FFFFFF',
  paid: '#FFFFFF',
  live: '#FFFFFF',
}

export const DEPOSIT_GROUP_LABEL: Record<DepositGroup, string> = {
  paid: 'Успешный',
  unpaid: 'Неуспешный',
  in_progress: 'В работе',
  no_payout: 'Не нашёл выплату',
  precheck_rejected: 'Откинут пречеком',
}

export const DEPOSIT_GROUP_COLOR: Record<DepositGroup, string> = {
  paid: DEPOSIT_RAMP[4],
  unpaid: DEPOSIT_RAMP[3],
  in_progress: DEPOSIT_RAMP[1],
  no_payout: REFUSED_DEPOSIT_COLOR,
  precheck_rejected: '#C3CDD7',
}

/** Флаги контроля потока из решения об отказе. */
export const FLOW_FLAG_LABEL: Record<string, string> = {
  competition: 'конкуренция',
  availability: 'доступность',
  pressure: 'давление',
}

/** Статусы заявки депозита — подписи для карточки. */
export const DEPOSIT_STATUS_LABEL: Record<string, string> = {
  COMPLETED: 'Зачислен',
  DECLINED: 'Отклонён',
  EXPIRED: 'Истёк',
  NOT_CONFIRMED: 'Не подтверждён',
  PAYMENT_PENDING: 'Ожидает оплаты',
  WAITING_FOR_ACTION: 'Ожидает действия',
  WAITING_FOR_SMS: 'Ожидает СМС',
  FILES_VERIFICATION: 'Проверка чека',
  FILES_AUTO_VERIFICATION: 'Автопроверка чека',
}

/** Подпись статуса депозита; незнакомый статус показывается кодом, как есть. */
export const depositStatusLabel = (status: string | null | undefined): string => {
  if (!status) return '—'

  return DEPOSIT_STATUS_LABEL[status] ?? status
}

/** Решение второго контура по депозиту. */
export const V2_ACTION_LABEL: Record<string, string> = {
  issue: 'Выдан реквизит',
  offer: 'Предложены плитки',
  refuse: 'Отказ',
}

/** Причины второго контура: отказ ядра по депозиту и по выплате (с префиксом v2_ и без). */
export const V2_REASON_LABEL: Record<string, string> = {
  no_candidates: 'нет выплат в пуле',
  all_gated: 'ни одна выплата не подошла по правилам',
  price_of_supply: 'не хватает притока депозитов',
  hold_cost: 'удержание выплаты дороже выгоды',
  negative_progress: 'сумма ухудшает шанс закрыть выплату',
  no_gain: 'нет выгоды',
  offer_declined: 'плитки не выбраны',
  reserved: 'ёмкость придержана для приоритетных',
  amount_limit: 'сумма выше лимита',
  admitted: 'принята',
  admitted_stretched: 'принята с растяжкой',
  MATCHING_PAYOUT_PERSONAL_RESTRICTED: 'персональный запрет клиента',
}

/** Подпись причины второго контура; неизвестный код показывается как есть. */
export const v2ReasonLabel = (code: string | null | undefined): string => {
  if (!code) return '—'
  const bare = code.startsWith('v2_') ? code.slice(3) : code
  return V2_REASON_LABEL[bare] ?? code
}

export const V2_PASS_LABEL: Record<string, string> = {
  preferred: 'выплаты запрошенного банка',
  full: 'весь пул',
}

/** Пункты легенды кликабельны; подсказка показывается один раз и помнится в браузере. */
export const LEGEND_HINT_STORAGE_KEY = 'matching.timeline.legendHintSeen'
export const LEGEND_HINT = 'Нажмите на пункт легенды, чтобы скрыть или показать его на графике'
